/**
 * ASYNC PROCESS NOTIFICATION PILL (Web)
 * In-chat notification pills for background process completions
 *
 * M11+L4 FIX (Mission 35B): Full restyle — replaced 44 hardcoded hex colors
 * with CSS variables from index.css, and replaced 6 imperative onMouseEnter/
 * onMouseLeave handlers with CSS :hover via a stylesheet.
 *
 * Design: Cedar card — green outer, subtle inner, white/primary action buttons
 * Mission 11: Unified Notifications
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { DataService } from '../../services/dataService';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/api';

/* ------------------------------------------------------------------ */
/* Stylesheet — all colors reference CSS variables from index.css      */
/* ------------------------------------------------------------------ */
const PILL_STYLE_ID = 'notif-pill-styles';

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PILL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PILL_STYLE_ID;
  style.textContent = `
    .notif-pill-outer {
      background-color: var(--cedar-light);
      border-radius: 16px;
      padding: 10px;
      margin-bottom: 12px;
      max-width: 600px;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .notif-pill-inner {
      background-color: var(--cedar-subtle);
      border-radius: 12px;
      padding: 16px;
    }
    .notif-pill-icon {
      color: var(--cedar-primary);
      flex-shrink: 0;
      width: 20px;
      margin-top: 2px;
    }
    .notif-pill-title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
      line-height: 1.4;
    }
    .notif-pill-timestamp {
      font-size: 12px;
      color: #666;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .notif-pill-desc {
      font-size: 14px;
      color: #666;
      line-height: 1.5;
    }
    .notif-pill-desc-label {
      font-weight: 600;
      color: #444;
    }
    .notif-pill-actions {
      display: flex;
      gap: 8px;
      padding: 10px 6px 4px 6px;
    }
    .notif-pill-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 16px;
      border: none;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: inherit;
      flex: 1;
    }
    .notif-pill-btn-secondary {
      background-color: white;
      color: var(--cedar-primary);
    }
    .notif-pill-btn-secondary:hover {
      background-color: var(--cedar-primary);
      color: white;
    }
    .notif-pill-btn-primary {
      background-color: var(--cedar-primary);
      color: white;
      font-weight: 500;
    }
    .notif-pill-btn-primary:hover {
      background-color: var(--cedar-accent-sky);
    }
  `;
  document.head.appendChild(style);
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface AsyncProcessNotification {
  id: string;
  type: string;
  title: string;
  description: string;
  deep_link_url: string;
  discussion_prompt: string;
  metadata: Record<string, any>;
  created_at: string;
}

interface AsyncProcessNotificationPillProps {
  notification: AsyncProcessNotification;
  onDeepLink: (url: string, notification: AsyncProcessNotification) => void;
  onDiscuss: (prompt: string, notificationId: string) => void;
  onAcknowledge: (notificationId: string, action: string) => void;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const AsyncProcessNotificationPill: React.FC<AsyncProcessNotificationPillProps> = ({
  notification,
  onDeepLink,
  onDiscuss,
  onAcknowledge
}) => {
  const navigate = useNavigate();

  // Inject styles once
  React.useEffect(() => { ensureStyles(); }, []);

  const handleDeepLink = () => {
    onAcknowledge(notification.id, 'deep_link_clicked');

    if (notification.type === 'plan_updated') {
      DataService.clearCache();
    }

    onDeepLink(notification.deep_link_url, notification);

    setTimeout(() => {
      const url = notification.type === 'plan_updated'
        ? `${notification.deep_link_url}${notification.deep_link_url.includes('?') ? '&' : '?'}refresh=true`
        : notification.deep_link_url;

      const isFromChat = window.location.pathname === '/chat';

      if (isFromChat) {
        if (url.startsWith('/goal/')) {
          navigate(`/goal/${url.replace('/goal/', '').split('?')[0]}`, { state: { source: 'chat', from: 'chat' } });
        } else if (url.startsWith('/plan/')) {
          navigate(`/plan/${url.replace('/plan/', '').split('?')[0]}`, { state: { source: 'chat', from: 'chat' } });
        } else if (url.startsWith('/workout/')) {
          navigate(`/workout/${url.replace('/workout/', '').split('?')[0]}`, { state: { source: 'chat', from: 'chat' } });
        } else {
          navigate(url, { state: { from: 'chat' } });
        }
      } else {
        navigate(url);
      }
    }, 200);
  };

  const handleDiscuss = () => {
    onAcknowledge(notification.id, 'discussion_started');
    onDiscuss(notification.discussion_prompt, notification.id);
  };

  const formatTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  /* ---- Shared card structure ---- */
  const PillCard: React.FC<{
    title: string;
    description: React.ReactNode;
    actions: React.ReactNode;
  }> = ({ title, description, actions }) => (
    <div className="notif-pill-outer">
      <div className="notif-pill-inner">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div className="notif-pill-icon">
            <Check size={20} strokeWidth={2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
              <div className="notif-pill-title">{title}</div>
              <div className="notif-pill-timestamp">{formatTimestamp(notification.created_at)}</div>
            </div>
            <div className="notif-pill-desc">{description}</div>
          </div>
        </div>
      </div>
      <div className="notif-pill-actions">{actions}</div>
    </div>
  );

  /* ---- Goal Created ---- */
  if (notification.type === 'goal_created') {
    const goalText = notification.description || notification.metadata?.goal_text || 'Your training goal';
    return (
      <PillCard
        title="Your first week's plan is ready"
        description={<><span className="notif-pill-desc-label">Your goal:</span> {goalText}</>}
        actions={
          <>
            <button
              className="notif-pill-btn notif-pill-btn-secondary"
              onClick={() => {
                onAcknowledge(notification.id, 'deep_link_clicked');
                setTimeout(() => navigate('/move', { state: { source: 'goal-notification', from: 'chat' } }), 200);
              }}
            >
              View plan
            </button>
            <button className="notif-pill-btn notif-pill-btn-primary" onClick={handleDiscuss}>
              Let's discuss
            </button>
          </>
        }
      />
    );
  }

  /* ---- Post-Workout Debrief ---- */
  if (notification.type === 'post_workout_debrief') {
    return (
      <PillCard
        title={notification.title || 'Workout Complete'}
        description={notification.description}
        actions={
          <button className="notif-pill-btn notif-pill-btn-primary" onClick={handleDiscuss}>
            Let's discuss
          </button>
        }
      />
    );
  }

  /* ---- Default (all other types) ---- */
  return (
    <PillCard
      title={notification.title}
      description={notification.description}
      actions={
        <>
          <button className="notif-pill-btn notif-pill-btn-secondary" onClick={handleDeepLink}>
            Take a look
          </button>
          <button className="notif-pill-btn notif-pill-btn-primary" onClick={handleDiscuss}>
            Let's discuss
          </button>
        </>
      }
    />
  );
};

/* ------------------------------------------------------------------ */
/* Container component for managing multiple notification pills        */
/* ------------------------------------------------------------------ */

interface AsyncProcessNotificationsProps {
  userId: string;
  onDiscussionStart: (message: string) => void;
}

export const AsyncProcessNotifications: React.FC<AsyncProcessNotificationsProps> = ({
  userId,
  onDiscussionStart
}) => {
  const [notifications, setNotifications] = useState<AsyncProcessNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const jwtToken = await storage.getItem('jwt_token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
        const response = await fetch(`${API_BASE_URL}/api/notifications/chat-pills/${userId}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [userId]);

  const handleDeepLink = (url: string, notification: AsyncProcessNotification) => {
    if (notification.type === 'plan_updated') {
      DataService.clearCache();
    }
    const urlParams = new URLSearchParams(url.split('?')[1] || '');
    urlParams.set('source', 'notification');
    urlParams.set('refresh', 'true');
    navigate(`${url.split('?')[0]}?${urlParams.toString()}`);
  };

  const handleDiscuss = async (prompt: string, notificationId: string) => {
    onDiscussionStart(prompt);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleAcknowledge = async (notificationId: string, action: string) => {
    try {
      const jwtToken = await storage.getItem('jwt_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
      await fetch(`${API_BASE_URL}/api/notifications/acknowledge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ notification_id: notificationId, user_id: userId, action })
      });
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  if (loading && notifications.length === 0) return null;
  if (notifications.length === 0) return null;

  return (
    <div style={{ marginBottom: '12px' }}>
      {notifications.map(notification => (
        <AsyncProcessNotificationPill
          key={notification.id}
          notification={notification}
          onDeepLink={handleDeepLink}
          onDiscuss={handleDiscuss}
          onAcknowledge={handleAcknowledge}
        />
      ))}
    </div>
  );
};

export default AsyncProcessNotificationPill;
